/**
 * Toast - Core Component (Enhanced Wrapper)
 *
 * This component wraps the shadcn/ui Sonner toast with project-specific styling.
 * Uses design tokens for consistent look and feel.
 *
 * Usage:
 * 1. Add <Toaster /> to your root layout
 * 2. Import { toast } from this file
 * 3. Call toast.success(), toast.error(), etc.
 *
 * @see {@link file://./../ui/sonner.tsx} for the underlying implementation
 */
'use client';

import { ComponentProps } from 'react';
import { toast as sonnerToast, type ExternalToast } from 'sonner';
import { Toaster as ShadcnToaster } from '@/app/_components/ui/sonner';

export type { ExternalToast };
export type ToasterProps = ComponentProps<typeof ShadcnToaster>;

/**
 * Toaster component - renders toast notifications.
 * Add this once to your root layout.
 */
export function Toaster(props: ToasterProps) {
  return (
    <ShadcnToaster
      position="bottom-right"
      richColors
      theme="light"
      className="pb-safe-bottom"
      style={{
        '--border-radius': 'var(--popover-radius, var(--radius))',
      } as React.CSSProperties}
      {...props}
    />
  );
}

/**
 * Toast helper functions for showing notifications.
 *
 * @example
 * ```tsx
 * import { toast } from '@/app/_components/core/toast';
 *
 * // Success toast
 * toast.success('File downloaded successfully');
 *
 * // Error toast
 * toast.error('Download failed');
 *
 * // With description
 * toast.error('Download failed', {
 *   description: 'Please check your connection and try again.'
 * });
 * ```
 */
export const toast = {
  success: (message: string, options?: ExternalToast) =>
    sonnerToast.success(message, options),

  error: (message: string, options?: ExternalToast) =>
    sonnerToast.error(message, options),

  info: (message: string, options?: ExternalToast) =>
    sonnerToast.info(message, options),

  warning: (message: string, options?: ExternalToast) =>
    sonnerToast.warning(message, options),

  loading: (message: string, options?: ExternalToast) =>
    sonnerToast.loading(message, options),

  dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
};
